/**
 * PaperListPanel — 시험지(/exam-papers) 마스터(계약 §5-1 · authoring §6).
 *
 * 검색·정렬은 **서버가 한다**(계약 §1-4 — GET /papers 에 q·sort·order 가 생겼다).
 * 그 전에는 파라미터가 page/per_page/mine 뿐이라 제목 거르기가 현재 페이지 20건 안에서만
 * 돌았고, "검색했는데 없다"를 막으려고 그 사실을 화면에 적어 두어야 했다. 이제는 전체에서
 * 찾으므로 그 안내 문구를 걷었다 — 남겨 두면 거짓말이 된다.
 *
 * 조회 조건이 여섯이 됐다(mine · folderId · includeChildren · q · sort · order). 그래서
 * load() 는 인자를 줄줄이 받지 않고 **criteriaRef 한 벌**을 읽고, 방금 바꾼 축만 override 로
 * 덮는다 — 상태 갱신은 다음 렌더에나 반영되므로 바꾼 그 자리에서는 ref 가 아직 옛 값이다.
 *
 * 모양이 둘이다(layout). 시험지 제작(레거시 프리셋)과 같은 결로 보이려면 표여야 하지만,
 * 마스터-디테일이 열린 좁은 폭(360~440px)에는 8컬럼이 물리적으로 안 들어간다 — 그 폭에서는
 * 카드 목록이 같은 정보를 두 줄로 접어 보여 준다.
 *
 * 행 액션은 여기서 실행하지 않고 prop 으로 올린다. 삭제·배포는 미저장 편집을 날릴 수 있어
 * 확인 흐름을 부모(페이지)가 쥐어야 하기 때문이다.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw, ChevronRight, ChevronDown, ChevronUp, Plus, Sparkles } from 'lucide-react';
import { Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { parseV2Timestamp } from '../../utils/v2DateTime';
import { listPapers, listBatches, fmtServerDateTime, PAPER_SORTS, PAPER_ORDERS, BATCH_STATUS_ONGOING } from './examPaperApi';
import PaperTable, { paperIdOf, paperHeadMeta, paperAssignCount } from './PaperTable';
import { PaperSearchSortControls, PaperScopeControls } from './PaperListControls';
import { PaperCardList, PaperListPagination, PaperListSkeleton } from './PaperCardList';
import { nextSort } from '../common/tableSort';

/**
 * '진행 중' = 아직 조립되지 않은 배치. 계약(contract-answers §1-2)이 배치 행에 result_paper_id 를
 * 주고, 조립이 끝나면 거기에 새 시험지 번호가 붙는다 — 그 뒤로는 돌아갈 작업이 아니라 결과물이다.
 * 배치의 status 값이 무엇을 뜻하는지는 계약에 없어(정의된 것은 멤버의 status 100/150/200/500 뿐)
 * 판정에 쓰지 않는다. 최신순인지도 적혀 있지 않아 created_at 으로 직접 세운다.
 */
function pickOngoing(items) {
  // 문자열로 견주면 'Z'와 '+09:00' 이 섞였을 때 순서가 뒤집힌다 — 시각으로 읽어 견준다.
  // 존 없는 옛 UTC 응답도 공통 파서로 같은 시각 값에 맞춘다.
  const startedMs = (b) => parseV2Timestamp(b?.created_at)?.getTime() ?? 0;
  return items
    .filter((b) => b?.batch_id != null && b.result_paper_id == null)
    .sort((a, b) => startedMs(b) - startedMs(a));
}

/**
 * 접힌 상태에서 상시로 보이는 줄 수. **3 → 5**(오너 지시 2026-08-29 — "이 리스트들은 빨리 처리해야
 * 되는 목록이므로 상시 보여주는 게 맞음"). 5 를 넘는 것은 지우지 않고 아래 버튼으로 펼친다 —
 * 전에는 "N개 더 있습니다"라고 **말만 하고 갈 길을 주지 않았다.**
 */
const ONGOING_SHOWN = 5;

/** 검색어를 서버로 보내기 전 기다리는 시간(ms). 한글은 한 글자에 조합 이벤트가 여러 번 온다 */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * 정렬 선택지 — 계약 §1-4 의 sort × order 조합이다.
 * 값이 두 축인데 셀렉트는 하나라 'sort:order' 로 붙여 싣는다. 첫 줄이 서버 기본(created_at desc)과
 * 같아야 한다 — 화면을 처음 열었을 때 고른 것과 실제 순서가 어긋나면 그것부터 거짓말이다.
 * 표기는 「무엇을 위로 올리는가」로 적는다. 'desc' 같은 원어를 셀렉트에 두면 매번 되짚어야 한다.
 *
 * **목록은 손으로 적지 않고 API 층 상수에서 만든다**(PAPER_SORTS·PAPER_ORDERS).
 * 그쪽은 허용값 밖이면 파라미터를 통째로 안 보내는 자리라, 두 곳을 손으로 맞춰 두면 한쪽만 늘었을 때
 * **화면에는 있는데 서버로는 안 가는 정렬**이 조용히 생긴다 — 골라도 목록이 그대로인데 이유가 화면에 없다.
 * 한국어 표기는 코드가 정해야 하므로 라벨 맵만 여기 둔다.
 */
const SORT_LABELS = {
  // 안쪽 키 순서가 곧 셀렉트 줄 순서다 — 날짜는 최신(desc)이 먼저, 제목은 ㄱ(asc)이 먼저 선다
  created_at: { desc: '최근 만든 순', asc: '먼저 만든 순' },
  updated_at: { desc: '최근 고친 순', asc: '오래 안 고친 순' },
  title: { asc: '제목 ㄱ→ㅎ', desc: '제목 ㅎ→ㄱ' },
  // 출제 = 학생에게 나간 배정 행 수(표의 「학생 출제」 아이콘과 같은 수). 많은 쪽을 먼저 세운다 —
  // 이 정렬을 고르는 까닭이 「어느 게 제일 많이 나갔나」이지 그 반대가 아니다
  assign_count: { desc: '출제 많은 순', asc: '출제 적은 순' },
};

const SORT_OPTIONS = PAPER_SORTS.flatMap((sort) => {
  const labels = SORT_LABELS[sort] || {};
  // 이름이 붙은 order 를 맵 순서대로 먼저 세우고, 상수에만 있는 order 를 뒤에 붙인다 —
  // 어느 쪽이 늘어도 빠지는 줄이 없다(겹치는 것은 처음 자리만 남긴다).
  const orders = [...Object.keys(labels), ...PAPER_ORDERS]
    .filter((o, i, all) => PAPER_ORDERS.includes(o) && all.indexOf(o) === i);
  // 라벨 맵에 없는 값은 **값을 그대로 라벨로** 세운다. 이름이 없다고 줄을 빼면 늘린 축을 아무도 못 쓴다
  return orders.map((order) => ({ value: `${sort}:${order}`, label: labels[order] || `${sort}:${order}` }));
});

/** 처음 걸리는 정렬 = 첫 줄(지금은 서버 기본 created_at desc). 목록에서 뽑아야 목록 밖 값이 될 수 없다 */
const SORT_DEFAULT = SORT_OPTIONS[0]?.value || 'created_at:desc';

/**
 * 표 머리 정렬(오너 결정 2026-09-30) — 표 칸 key → 서버 sort. **서버가 세울 수 있는 칸만** 머리가 눌린다.
 * #ID·문항수·폴더·작성자는 서버에 정렬 값이 없다 — 이 쪽 20건 안에서만 세우면 전체 차례인 양 보여서 켜지 않는다
 * (PaperTable 머리 주석). 서버 허용값(PAPER_SORTS) 밖은 걸러 둔다 — 셀렉트와 같은 이유(위 SORT_OPTIONS 주석).
 *
 * 머리는 따로 상태를 두지 않고 셀렉트의 sortKey 를 읽고 쓴다. 둘이 각자 들면 머리 화살표와 셀렉트 글자가
 * 다른 정렬을 말한다. 차례는 다른 표와 같다(내림차순 → 오름차순 → 기본). 기본은 셀렉트의 첫 줄(SORT_DEFAULT)이다.
 */
const HEADER_SORT = Object.fromEntries(
  Object.entries({ title: 'title', updated: 'updated_at' }).filter(([, s]) => PAPER_SORTS.includes(s)),
);
const HEADER_SORT_KEYS = Object.keys(HEADER_SORT);

function PaperListPanel({
  selectedId,
  onSelect,
  onCreate,
  onPreview = null,
  previewBusyId = null,
  onAiCopy,
  onDelete = null,
  onOpenBatch = null,
  onMoveTo = null,
  folders = [],
  onCopy = null,
  /* (paperId) => void — 주면 표의 제목 앞에 「학생 출제」 아이콘이 선다. 배지 숫자의 해석은
     이 층이 안다(paperAssignCount) — 목록 항목의 필드를 읽는 일이라 페이지가 알 필요가 없다.
     페이지가 쥐는 것은 **어디로 갈 것인가** 하나뿐이다. */
  onOpenAssign = null,
  folderId = null, // 정수(그 폴더) | 'none'(무소속) | null(전체)
  /* 고른 폴더의 **자손 폴더까지** 함께 볼 것인가(계약 §2 include_children).
     폴더 트리가 올려 주는 값이고, folderId 가 정수일 때만 뜻이 있다 —
     'none'(무소속)·전체에는 자손이라는 개념이 없어 API 층이 알아서 파라미터를 뺀다. */
  includeChildren = false,
  /* 「하위 폴더 포함」 스위치가 이 머리줄에 선다(오너 지시 2026-08-28 — 트리 밑에서 옮겨 왔다).
     조건은 부모가 센다: 고른 것이 폴더이고 하위가 있을 때만 kidCount > 0 이다. */
  folderKidCount = 0,
  folderTotalCount = null,
  onIncludeChildrenChange = null,
  layout = 'card',
  reloadKey = 0,
  routeKey = '',
  selectedTitle = null,      // 상세가 열려 있을 때 머리줄에 적을 이름
  collapsed = false,         // 목록이 접혀 있는가(위아래 분할에서만 참)
  onToggleCollapse = null,   // 없으면 머리줄은 스위치가 아니라 그냥 제목이다
  chromeless = false,        // 카드 껍질(면·모서리·그림자)을 담는 쪽이 두를 때 참
  onHeaderHeight = null,     // 접혔을 때 남길 높이 — 머리줄 실측을 담는 쪽에 올린다
  selectedPaper = null,      // 고른 시험지의 상세(목록 페이지 밖에 있을 때의 폴백)
}) {
  const { getAccessToken } = useAuth();
  const [rows, setRows] = useState({ items: [], page: 1, totalPages: 1, totalItems: 0 });
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');          // 타자 그대로 — 화면의 입력칸이 쥔 값
  const [qApplied, setQApplied] = useState(''); // 서버에 실제로 보낸 검색어(디바운스를 지난 것)
  const [sortKey, setSortKey] = useState(SORT_DEFAULT); // 'sort:order' 한 덩어리
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seqRef = useRef(0);
  // 접히면 이 줄만 남는다 — 높이를 상수로 박으면 글자·버튼이 바뀔 때 조용히 어긋나므로 실측해 올린다
  const headRowRef = useRef(null);
  const pageRef = useRef(1); // 외부 갱신(저장·삭제·조립)에서 보던 페이지를 지킨다
  // AI 복사는 문항 수 × 수십 초짜리 작업이라 화면을 떠나는 것이 정상이다 — 배치 목록(§1-2)으로
  // 아직 조립되지 않은 배치를 찾아 돌아갈 줄을 그린다. 진행중(status 100)만 읽는다 — 폐기한 배치도
  // 시험지가 없어서, result_paper_id 만 보면 계속 진행중으로 남는다(BE-20261102 회신 §3).
  const [ongoing, setOngoing] = useState([]);
  // 5줄 뚜껑을 열어 둔 상태인가. 목록이 다시 5개 이하로 줄면 아래에서 무시하므로 되돌릴 필요가 없다
  const [ongoingOpen, setOngoingOpen] = useState(false);
  const batchSeqRef = useRef(0);

  const loadBatches = useCallback(async () => {
    const seq = (batchSeqRef.current += 1);
    try {
      const d = await listBatches({ status: BATCH_STATUS_ONGOING, perPage: 20 }, getAccessToken);
      if (seq !== batchSeqRef.current) return;
      setOngoing(pickOngoing(d.items));
    } catch (_) {
      // 보조 경로다 — 실패해도 시험지 목록은 그대로 보여야 하므로 줄만 접는다
      if (seq === batchSeqRef.current) setOngoing([]);
    }
  }, [getAccessToken]);

  // 이 패널은 화면 안에서 계속 살아 있으므로 경로가 바뀔 때도 다시 읽는다 —
  // 배치에서 빠져나오거나 조립이 끝나 그 줄이 사라진 것을 그때 반영한다.
  // onOpenBatch 가 없으면 「진행중인 AI변형 시험지」 줄 자체를 그리지 않는다(421줄) — 그때는 부르지도 않는다.
  // 안 그릴 것을 읽으면 왕복만 늘고, 권한이 닫힌 계정에서는 실패만 쌓인다.
  useEffect(() => { if (onOpenBatch) loadBatches(); }, [onOpenBatch, loadBatches, reloadKey, routeKey]);

  // 펼침은 **넘칠 때만** 뜻이 있다. 열어 둔 채 목록이 5개 이하로 줄어도 보이는 것은 같으므로
  // 상태를 되돌리는 정리 코드를 두지 않는다 — 되돌릴 자리가 늘면 그때부터 어긋난다.
  const ongoingOverflow = ongoing.length > ONGOING_SHOWN;
  const ongoingShown = ongoingOpen && ongoingOverflow ? ongoing : ongoing.slice(0, ONGOING_SHOWN);

  /**
   * 지금 화면이 걸어 둔 조회 조건 한 벌. **렌더마다 갱신한다.**
   *
   * load 를 부르는 자리가 여덟이 넘는데(새로고침·페이지 넘김·다시 시도·범위·폴더·검색·정렬·외부 신호)
   * 인자를 여섯 개씩 늘어놓으면 한 자리만 빠뜨려도 그 조건이 조용히 풀린 목록이 뜬다.
   * 그래서 조건은 여기서 한 벌로 읽고, **방금 바꾼 축만** override 로 덮는다 —
   * setState 는 다음 렌더에나 반영되므로 바꾼 그 자리에서는 이 ref 가 아직 옛 값이기 때문이다.
   */
  const criteriaRef = useRef(null);
  const [sort, order] = sortKey.split(':');
  criteriaRef.current = { mine, folderId, includeChildren, q: qApplied, sort, order };

  const load = useCallback(async (page, override = {}) => {
    const seq = (seqRef.current += 1);
    const c = { ...criteriaRef.current, ...override };
    setLoading(true);
    setError(null);
    try {
      const d = await listPapers({
        page,
        perPage: 20,
        mine: c.mine,
        folderId: c.folderId,
        includeChildren: c.includeChildren,
        q: c.q,
        sort: c.sort,
        order: c.order,
      }, getAccessToken);
      if (seq !== seqRef.current) return;
      setRows(d);
      pageRef.current = d.page ?? page;
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(e.message || '시험지 목록을 불러오지 못했습니다.');
      setRows({ items: [], page: 1, totalPages: 1, totalItems: 0 });
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [getAccessToken]);

  // 머리줄 실측 — 폰트 로딩·버튼 줄바꿈으로 높이가 바뀌므로 한 번 재고 끝내지 않는다
  useEffect(() => {
    const el = headRowRef.current;
    if (el == null || typeof onHeaderHeight !== 'function') return undefined;
    const report = () => onHeaderHeight(el.getBoundingClientRect().height);
    report();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeaderHeight, selectedTitle]);

  // 최초 1회 + 외부 갱신 신호. 페이지를 1로 되돌리지 않는다(3페이지에서 저장했는데 1페이지로
  // 튀면 방금 고친 시험지를 다시 찾아야 한다).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 의도: 외부 신호에만 반응(조건은 criteriaRef 가 쥔다)
  useEffect(() => { load(pageRef.current); }, [reloadKey]);
  // mine 토글은 결과 집합 자체가 바뀌므로 1페이지부터
  const toggleMine = (v) => { setMine(v); pageRef.current = 1; load(1, { mine: v }); };
  /**
   * 폴더 · 하위 포함(§2)을 바꾸면 결과 집합 자체가 달라진다 — 보던 페이지를 지키면
   * 범위 밖 페이지를 요청하게 된다. 두 축을 한 effect 에서 함께 본다: 트리에서 폴더를 누르면
   * 둘이 **같은 렌더에** 바뀌므로, 따로 두면 같은 조회가 두 번 나간다.
   */
  const folderRef = useRef(folderId);
  const kidsRef = useRef(includeChildren);
  useEffect(() => {
    if (String(folderRef.current) === String(folderId) && kidsRef.current === includeChildren) return;
    folderRef.current = folderId;
    kidsRef.current = includeChildren;
    pageRef.current = 1;
    load(1, { folderId, includeChildren });
  }, [folderId, includeChildren, load]);

  /**
   * 검색(계약 §1-4) — 타자마다 서버를 때리지 않게 350ms 기다렸다 보낸다.
   *
   * 보낸 값을 qApplied 로 따로 들고, 그것과 같으면 아무 일도 하지 않는다. 그래서
   * ① 첫 렌더(둘 다 빈 문자열)에서 조회가 한 번 더 나가지 않고
   * ② 다른 이유로 이 컴포넌트가 다시 그려져도 같은 검색이 되풀이되지 않는다.
   * [X]로 지우는 것도 같은 길을 지난다 — 즉시 보내면 예약된 타이머와 순서가 엇갈려
   * 지운 뒤에 옛 검색 결과가 들어앉는 창이 생긴다.
   */
  useEffect(() => {
    const term = q.trim();
    if (term === qApplied) return undefined;
    const t = setTimeout(() => {
      setQApplied(term);
      pageRef.current = 1; // 검색이 바뀌면 1페이지로 — 3페이지에서 걸면 대부분 빈 페이지가 나온다
      load(1, { q: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, qApplied, load]);

  // 정렬은 전체 순서를 뒤집으므로 보던 페이지가 뜻을 잃는다 — 1페이지부터 다시 센다
  const changeSort = (v) => { setSortKey(v); pageRef.current = 1; const [s, o] = v.split(':'); load(1, { sort: s, order: o }); };
  // 표 머리가 보는 정렬 — 셀렉트가 머리 칸의 정렬을 고르고 있을 때만 그 칸에 화살표가 선다(HEADER_SORT 주석)
  const headerCol = HEADER_SORT_KEYS.find((k) => HEADER_SORT[k] === sort);
  const headerSort = headerCol ? { key: headerCol, order } : null;
  const changeHeaderSort = (colKey) => {
    const next = nextSort(headerSort, colKey);
    changeSort(next ? `${HEADER_SORT[next.key]}:${next.order}` : SORT_DEFAULT);
  };

  // 접히면 머리줄 한 줄만 남긴다(오너 지시) — 나머지는 잘려서 안 보이지만,
  // visibility 를 함께 내려야 탭 포커스가 안 보이는 칸으로 들어가지 않는다.
  // (display:none 이면 접히는 도중 내용이 툭 사라지고, opacity:0 이면 탭이 들어간다.)
  const bodyStyle = collapsed ? { visibility: 'hidden' } : undefined;

  /**
   * 접히면 표가 통째로 가려져 문항수·판·폴더·작성자·수정일을 볼 수 없다(오너 지적).
   * 그래서 머리줄에 같은 값을 칩으로 단다.
   *
   * 값은 **목록 행에서 먼저** 찾는다 — 표와 한 곳에서 나와야 둘이 어긋나지 않는다.
   * 고른 시험지가 다른 페이지에 있어 이 페이지 20건에 없을 때만 상세 응답으로 떨어진다.
   */
  const headPaper = useMemo(() => {
    if (selectedId == null) return null;
    const inPage = rows.items.find((p) => String(paperIdOf(p)) === String(selectedId));
    return inPage || selectedPaper || null;
  }, [rows.items, selectedId, selectedPaper]);

  const headMeta = useMemo(() => paperHeadMeta(headPaper), [headPaper]);

  // 목록은 서버가 이미 거른 것을 그대로 그린다(§1-4) — 여기서 한 번 더 거르면
  // 「총 N개」와 눈에 보이는 수가 또 어긋나고, 그 어긋남을 다시 설명해야 한다.

  /**
   * 0건 안내 — 「검색이 못 찾았다」와 「원래 없다」를 구분한다. 서버 검색이 된 뒤로도
   * 이 구분은 남는다: 폴더를 눌렀는데 옛 검색어가 그대로 걸려 0건인 것을 폴더가 빈 것으로 읽는다.
   */
  const emptyNode = (
    <>
      <p className="text-sm text-gray-400">
        {qApplied
          ? `'${qApplied}' 에 맞는 시험지가 없습니다.`
          : mine ? '내가 만든 시험지가 없습니다.' : '시험지가 없습니다.'}
      </p>
      {qApplied
        ? (
          <div className="mt-2">
            <Button size="xs" variant="secondary" onClick={() => setQ('')}>검색어 지우기</Button>
          </div>
        )
        : (
          <div className="mt-2">
            <Button size="xs" variant="secondary" onClick={onCreate}>첫 시험지 만들기</Button>
          </div>
        )}
    </>
  );

  // 레거시 시험지제작 관례 — 전체 건수와 지금 눈에 보이는 건수를 함께 적는다.
  // 이제 둘이 벌어지는 이유는 페이지 나눔 하나다(검색은 서버가 하므로 「총 N개」도 검색 결과의 수다).
  const countText = loading
    ? '읽는 중…'
    : rows.totalItems > 0
      ? `총 ${rows.totalItems}개 중 ${rows.items.length}개 표시 중`
      : `${rows.items.length}개 표시 중`;

  return (
    /* chromeless — 상세와 한 장의 카드를 나눠 쓸 때는 껍질을 담는 쪽이 두른다.
       여기서도 두르면 카드 안에 카드가 들어가 테두리가 두 겹으로 보인다. */
    <div className={`flex h-full min-h-0 flex-col p-3 ${chromeless ? '' : 'rounded-3xl bg-white shadow-lg'}`}>
      <div className="shrink-0 space-y-2">
        <div ref={headRowRef} className="flex items-center gap-2">
          {/* 고른 것이 있으면 머리줄이 「시험지 › 이름」이 되고, 그 줄 자체가 펼침/접힘 스위치다.
              오른쪽 새로고침·[새 시험지]와 겹치지 않도록 스위치는 왼쪽 덩어리에만 건다. */}
          {/* 브레드크럼은 **접혀 있을 때만** 선다(오너 지시) — 표가 보이는 동안에는 제목·문항수·
              폴더·수정일이 표에 그대로 있어 같은 말이 두 번 된다.
              대신 접는 길은 남겨야 한다. 이 줄이 곧 펼침/접힘 스위치였어서, 그냥 감추면
              펼친 뒤 다시 아래를 볼 방법이 사라진다(같은 시험지라 행을 눌러도 아무 일이 없다). */}
          {selectedTitle && onToggleCollapse && collapsed ? (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-expanded={!collapsed}
              title={collapsed ? '목록을 펼칩니다' : '목록을 접고 아래를 봅니다'}
              className="flex min-w-0 items-center gap-1 rounded-full py-0.5 pl-1 pr-2 text-left transition-colors hover:bg-gray-100"
            >
              <span className="shrink-0 text-sm font-bold text-gray-800">시험지</span>
              <ChevronRight className="size-3 shrink-0 text-gray-300" aria-hidden="true" />
              {/* 제목과 요약은 한 흐름이다 — 배지로 떼어 놓으면 머리줄이 조각으로 읽히고,
                  좁아질 때 무엇을 먼저 버릴지도 정할 수 없다. 한 줄로 두면 뒤에서부터 잘린다. */}
              <span className="min-w-0 truncate text-sm">
                <span className="font-bold text-primary">{selectedTitle}</span>
                {headMeta && <span className="ml-2 text-xs text-gray-500">{headMeta}</span>}
              </span>
              <ChevronDown
                className={`size-3.5 shrink-0 text-gray-400 transition-transform duration-300 ${collapsed ? '' : 'rotate-180'}`}
                aria-hidden="true"
              />
            </button>
          ) : (
            /* 여기에 꺾쇠를 두지 않는다 — 목록이 펴져 있을 때 상세를 다시 여는 일은
               아래 손잡이(renderDetailBar)가 맡는다. 둘을 다 두면 같은 조작이 두 자리에 있고,
               아래쪽이 무엇을 여는지 이름까지 말하므로 그쪽이 낫다. */
            <h2 className="shrink-0 text-sm font-bold text-gray-800">시험지</h2>
          )}

          {/* 하위 폴더 포함(계약 §2 include_children) — 고른 폴더에 하위가 있을 때만 뜻이 있다.
              하위가 없는 폴더에도 그리면 눌러도 목록이 그대로인 자리가 된다.
              머리줄은 좁다(360~440px) — 글자를 「하위 n개 포함」으로 줄이고, 자손 포함 시험지 수는
              배지로 뒤에 붙인다. 전체 문구는 title 로 남긴다. */}
          {folderKidCount > 0 && onIncludeChildrenChange && (
            <label
              title={`하위 폴더 ${folderKidCount}개의 시험지까지 함께 봅니다`}
              className="flex min-w-0 shrink cursor-pointer items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-[10px] text-gray-600"
            >
              <input
                type="checkbox"
                checked={includeChildren}
                onChange={(ev) => onIncludeChildrenChange(ev.target.checked)}
                className="size-3 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="min-w-0 truncate">하위 {folderKidCount}개 포함</span>
              {folderTotalCount != null && (
                <span className="shrink-0 rounded-full bg-gray-100 px-1.5 font-medium text-gray-600">
                  {folderTotalCount}
                </span>
              )}
            </label>
          )}

          <button
            type="button"
            onClick={() => load(rows.page)}
            disabled={loading}
            aria-label="목록 새로고침"
            title="새로고침"
            className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <Button size="xs" onClick={onCreate}>
            <Plus className="mr-0.5 inline size-3" />
            새 시험지
          </Button>
        </div>

        {/* 범위·건수와 검색·정렬은 학생 허브의 선택창도 같은 표시 부품을 쓴다.
            조회 상태와 API는 이 패널에 그대로 남겨 페이지 전용 행동이 밖으로 새지 않는다. */}
        <PaperScopeControls
          mine={mine}
          onMineChange={toggleMine}
          countText={countText}
          style={bodyStyle}
        />

        {/* 검색과 정렬은 같은 축이다 — 둘 다 「목록에 무엇이 어떤 순서로 서는가」를 정한다.
            한 줄에 두면 결과가 달라 보일 때 어디를 만져야 하는지가 한눈에 붙는다.
            (형제 화면 ProblemPickerModal 의 검색줄과 같은 모양·같은 클래스다.) */}
        <PaperSearchSortControls
          query={q}
          onQueryChange={setQ}
          sortKey={sortKey}
          onSortChange={changeSort}
          sortOptions={SORT_OPTIONS}
          style={bodyStyle}
        />
      </div>

      {onOpenBatch && ongoing.length > 0 && (
        <div className="mt-2 shrink-0 space-y-1" style={bodyStyle}>
          <p className="text-[10px] font-medium text-violet-700">진행중인 AI변형 시험지</p>
          {/* 펼치면 줄이 몇 개든 늘어나는데 이 블록은 shrink-0 이라 아래 시험지 목록을 그대로 밀어낸다.
              그래서 **펼친 동안에만** 높이 뚜껑을 씌우고 안쪽을 굴린다. 뚜껑을 화면 높이의 비율로 잡은
              것은 줄 높이를 px 로 박으면 글자·여백이 바뀔 때 조용히 어긋나기 때문이다. */}
          <div className={ongoingOpen ? 'max-h-[40vh] space-y-1 overflow-y-auto pr-1' : 'space-y-1'}>
            {ongoingShown.map((b) => {
              const done = b.counts?.done;
              const total = b.counts?.total;
              const started = fmtServerDateTime(b.created_at);
              return (
                <button
                  key={b.batch_id}
                  type="button"
                  onClick={() => onOpenBatch(b.batch_id)}
                  aria-label={`AI 복사 배치 #${b.batch_id} 로 돌아가기`}
                  className="flex w-full items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-left text-[11px] text-violet-700 transition-colors hover:bg-violet-100"
                >
                  <Sparkles aria-hidden="true" className="size-3 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">
                    {b.title || '이름 없는 배치'}
                    {started && <span className="ml-1 text-[10px] text-violet-400">{started}</span>}
                  </span>
                  {typeof total === 'number' && total > 0 && (
                    <span className="shrink-0 text-[10px] text-violet-500">생성 {done ?? 0}/{total}</span>
                  )}
                  <span className="shrink-0 font-mono text-[10px] text-violet-400">#{b.batch_id}</span>
                </button>
              );
            })}
          </div>
          {/* 모양은 형제(ClampedText·ProblemBodyPreview)의 「모두 보기 / 접기」와 같은 줄이고,
              색만 이 블록의 보라로 맞췄다. 글자색이 형제의 gray-400(2.54:1)이 아니라 violet-600 인
              것은 신규 라인이라 대비 하한 4.5:1 을 지킨 것이다(흰 바탕 위 5.55:1). */}
          {ongoingOverflow && (
            <button
              type="button"
              onClick={() => setOngoingOpen((v) => !v)}
              aria-expanded={ongoingOpen}
              className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-violet-600 transition-colors hover:bg-violet-100"
            >
              {ongoingOpen
                ? <ChevronUp aria-hidden="true" className="size-3" />
                : <ChevronDown aria-hidden="true" className="size-3" />}
              {ongoingOpen ? '접기' : `조립하지 않은 배치 ${ongoing.length - ONGOING_SHOWN}개 더 보기`}
            </button>
          )}
        </div>
      )}

      <div className="mt-2 min-h-0 flex-1 overflow-y-auto" style={bodyStyle}>
        {error && (
          <div className="space-y-2 p-3">
            <p role="alert" className="text-sm text-danger">{error}</p>
            <Button size="xs" variant="secondary" onClick={() => load(rows.page)}>다시 시도</Button>
          </div>
        )}
        {/* 첫 로딩 스켈레톤 — 표 모양에서는 안 띄운다. 표 머리가 늘 서 있으므로 그 아래에
            스켈레톤까지 깔면 빈 표와 겹쳐 두 겹으로 보인다(표는 자기 안에 '읽는 중'을 적는다). */}
        {!error && loading && rows.items.length === 0 && layout !== 'table' && (
          <PaperListSkeleton />
        )}
        {/* 빈 안내 — 표 모양에서는 표 **안**에 세운다(머리를 남기려고). 카드 모양에서는 그대로 */}
        {!error && !loading && rows.items.length === 0 && layout !== 'table' && (
          <div className="space-y-2 p-6 text-center">{emptyNode}</div>
        )}

        {/* 오류면 표도 카드도 그리지 않는다 — 위의 오류 블록이 대신 말한다.
            (삼항에 !error 를 붙이면 오류일 때 else 인 카드 목록으로 떨어져,
             표 모양인데 카드가 렌더되고 빈 안내는 layout 조건에 걸려 안 뜬다 = 빈 화면.) */}
        {error ? null : layout === 'table' ? (
          <PaperTable
            items={rows.items}
            empty={loading ? <p className="text-sm text-gray-400">읽는 중…</p> : emptyNode}
            selectedId={selectedId}
            onSelect={onSelect}
            onPreview={onPreview}
            previewBusyId={previewBusyId}
            onAiCopy={onAiCopy}
            onDelete={onDelete}
            onMoveTo={onMoveTo}
            folders={folders}
            onCopy={onCopy}
            assignCountOf={paperAssignCount}
            onOpenAssign={onOpenAssign}
            sort={headerSort}
            onSort={changeHeaderSort}
            sortableKeys={HEADER_SORT_KEYS}
          />
        ) : (
          <PaperCardList
            items={rows.items}
            selectedId={selectedId}
            onSelect={onSelect}
            onPreview={onPreview}
            previewBusyId={previewBusyId}
            onAiCopy={onAiCopy}
          />
        )}
      </div>

      <PaperListPagination
        page={rows.page}
        totalPages={rows.totalPages}
        loading={loading}
        onPageChange={load}
        style={bodyStyle}
      />
    </div>
  );
}

export default PaperListPanel;
